'use client';

import { useRouter } from 'next/navigation';
import styles from './carousel.module.css';
import { Text } from '@/components/Text/Text';
import type { ThemeBreakpoints } from '@frameio/vapor-theme';
import {
  Carousel as VaporCarousel,
  CarouselItem,
  RouterProvider,
  useActiveWindowBreakpoint,
} from '@frameio/vapor';
import type { FetchedComponentPages } from 'app/(user)/_queries/fetch-component-pages';
import { slugToPath } from '@/components/slugToPath';
import Link from 'next/link';
import { SanityImage, SanityImageSource } from '@/components/SanityImage';

type CarouselProps = {
  pages: FetchedComponentPages;
};

type CarouselCardProps = {
  title: string;
  description?: string;
  image?: SanityImageSource;
  width: number;
  height: number;
  breakpoint: ThemeBreakpoints;
};

const DEFAULT_ITEM_WIDTH = 450;
const DEFAULT_ITEM_HEIGHT = 360;
const ITEM_RATIO = DEFAULT_ITEM_WIDTH / DEFAULT_ITEM_HEIGHT;

const TABLET_WIDTH = 240;
const MOBILE_WIDTH = 180;

const BREAKPOINT_WIDTHS = {
  base: MOBILE_WIDTH,
  tablet: TABLET_WIDTH,
  desktop: DEFAULT_ITEM_WIDTH,
};

function CarouselCard({
  title,
  description,
  image,
  width,
  height,
  breakpoint,
}: CarouselCardProps) {
  return (
    <div
      className={styles.carouselCard}
      style={
        {
          '--carousel-card-width': `${width || DEFAULT_ITEM_WIDTH}px`,
          '--carousel-card-height': `${height || DEFAULT_ITEM_HEIGHT}px`,
        } as React.CSSProperties
      }
    >
      <div className={styles.imageWrapper}>
        {image && (
          <SanityImage
            alt=""
            aria-hidden="true"
            className={styles.image}
            // preserving the dimensions of the image in relation to the card for all breakpoints (based on the desktop design)
            height={height / 1.2}
            quality={100}
            src={image}
            // preserving the dimensions of the image in relation to the card for all breakpoints (based on the desktop design)
            width={width / 0.9}
          />
        )}
      </div>
      <div className={styles.meta}>
        <Text className={styles.heading} color="primary" variant="subtitle1">
          {title}
        </Text>
        {description && breakpoint === 'desktop' && (
          <Text className={styles.description} color="tertiary" variant="body2">
            {description}
          </Text>
        )}
      </div>
    </div>
  );
}

export function Carousel({ pages }: CarouselProps) {
  const router = useRouter();
  const windowBreakpoint = useActiveWindowBreakpoint();
  const itemWidth = BREAKPOINT_WIDTHS[windowBreakpoint];
  const itemHeight = itemWidth / ITEM_RATIO;

  return (
    <RouterProvider
      LinkComponent={Link as any}
      navigate={(href) => {
        router.push(href);
      }}
    >
      <VaporCarousel
        key={`${itemWidth}-${itemHeight}`}
        items={pages}
        itemWidth={itemWidth}
        itemHeight={itemHeight}
        itemGap={16}
        variant="unstyled"
      >
        {(page) => (
          <CarouselItem
            key={page._id}
            className={styles.carouselItem}
            href={slugToPath([
              page.parentSection.category.slug,
              page.parentSection.slug,
              page.slug,
            ])}
            textValue={page.title}
          >
            <CarouselCard
              {...page}
              width={itemWidth}
              height={itemHeight}
              breakpoint={windowBreakpoint}
            />
          </CarouselItem>
        )}
      </VaporCarousel>
    </RouterProvider>
  );
}
